import React, { useState } from 'react';
import { BookingOrderByOption } from 'Util/_HumanWritten/Bookings/FetchBookingsUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Button } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';

export interface SortableHeaderButtonProps<T = string> {
	/**
	 * Display text for the column header
	 */
	text: string;

	/**
	 * The field value to sort by (e.g., 'CreatedDate', 'Amount', or enum value)
	 */
	orderByField: T;

	/**
	 * Current sort field (e.g., 'CreatedDate', or enum value)
	 */
	currentOrderBy: T;

	/**
	 * Is the current sort in descending order?
	 */
	isDescending: boolean;

	/**
	 * Callback when header is clicked
	 * Receives: (orderByField, isDescending)
	 */
	onSort: (orderByField: T, isDescending: boolean) => void;
}

/**
 * Sortable column header button with visual indicators
 *
 * Shows:
 * - Current sort direction with arrow (↑ for ascending, ↓ for descending)
 * - Dimmed sort icon on hover for non-sorted columns
 * - Bold text for current sort column
 * - Clickable to toggle sort direction
 *
 * Behavior:
 * - Click same column: toggle sort direction (ASC ↔ DESC)
 * - Click different column: switch to that column (defaults to DESC)
 *
 * Usage:
 * <SortableHeaderButton
 *   text="Created Date"
 *   orderByField="CreatedDate"
 *   currentOrderBy={filters.orderBy}
 *   isDescending={filters.orderByDescending}
 *   onSort={(field, desc) => updateFilters({...filters, orderBy: field, orderByDescending: desc})}
 * />
 */
export function SortableHeaderButton<T = string>({
	text,
	orderByField,
	currentOrderBy,
	isDescending,
	onSort,
}: SortableHeaderButtonProps<T>) {
	const [isHovered, setIsHovered] = useState(false);
	const isCurrentSort = currentOrderBy === orderByField;

	const handleClick = () => {
		if (isCurrentSort) {
			// Clicking same column: toggle direction
			onSort(orderByField, !isDescending);
		} else {
			// Clicking different column: default to descending
			onSort(orderByField, true);
		}
	};

	return (
		<div className="staff-bookings__header-order-btn-wrap">
			{text}
			<If condition={!!orderByField}>
				<Button
					className="staff-bookings__header-order-btn"
					// eslint-disable-next-line no-nested-ternary
					icon={{
						icon: isCurrentSort ? (isDescending ? 'arrow-down' : 'arrow-up') : 'order',
						iconPos: 'icon-right',
					}}
					onClick={handleClick}
				/>
			</If>
		</div>
	);
}
